import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type { Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import type { CartPackageTextNodeDisplayModel } from '@shared/models/carts/cartPackageTextNotes';
import type { PackageItemDisplayModel } from '@shared/models/packages/packageItems';
import type {
  PackageItemImageNodeDisplayModel,
  PackageItemTextNodeDisplayModel,
} from '@shared/models/packages/packageNodes';
import type { IPackageModelProperties, PackageDisplayModel } from '@shared/models/packages/packages';
import type { SubjectImageDisplayModel } from '@shared/models/subjects/images';
import type { SubjectFieldCreateModel, SubjectFieldDisplayModel } from '@shared/models/subjects/subjectFields';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';
import {
  isGroupImageNode,
  isIndividualImageNode,
  isValidImageNode,
  isAutoFilledIndividualImageNode,
} from '@shared/services/packageNodes.service';
import type { EntrySubjectModel } from '@shared/models/entry/entry';
import {
  fillAutoFilledTextNode,
  fillTextNode,
  type SubjectCoreColumns,
  type TextNodeFillOptions,
} from './cartPackageNode.service';

// Multi-image download text node names (must match backend ProductCodes constants)
const SELECTED_IMAGE_IDS_NODE_NAME = 'SelectedImageIds';
const MAX_IMAGE_QUANTITY_NODE_NAME = 'MaxImageQuantity';
const ALLOW_GROUPS_NODE_NAME = 'AllowGroups';

//TODO: mapTextNode and mapImageNode are setting the cartpackage node id to the package node id. This is not correct. This is a bug.

/**
 * Maps a PackageItemTextNode to a CartPackageTextNode.
 * For multi-image settings nodes (MaxImageQuantity, AllowGroups), preserves the
 * original text value so the frontend can read the settings.
 * For editable/selection nodes, initializes with empty value.
 */
export const mapTextNode = (
  node: PackageItemTextNodeDisplayModel,
  // packageItemId: number,
): CartPackageTextNodeDisplayModel => {
  const isSettingsNode = node.name === MAX_IMAGE_QUANTITY_NODE_NAME || node.name === ALLOW_GROUPS_NODE_NAME;
  const value = isSettingsNode ? (node.text ?? '') : '';

  return {
    ...node,
    packageItemTextNodeId: node.id,
    value,
  };
};

export const mapImageNode = (
  node: PackageItemImageNodeDisplayModel,
  // packageItemId: number,
): CartPackageImageNodeDisplayModel => ({
  ...node,
  packageItemImageNodeId: node.id,
  groupImageId: null,
  subjectImageId: null,
  replacementImageId: null,
  frontendCropDimensions: null,
  cropDimensions: null,
  //   cartPackageId: null,
  imageThumbnailUrl: '',
  imageUrl: '',
  //   cropDimensions: '',
  // imageNodeData: node,
  // packageItemId,
});

/**
 * Determines if a text node should be included in the cart package.
 * Includes editable nodes plus multi-image download nodes (settings and selection).
 */
const MULTI_IMAGE_NODE_NAMES = [SELECTED_IMAGE_IDS_NODE_NAME, MAX_IMAGE_QUANTITY_NODE_NAME, ALLOW_GROUPS_NODE_NAME];

// Settings nodes contain configuration values, not subject data - should not be auto-filled
const SETTINGS_NODE_NAMES = [MAX_IMAGE_QUANTITY_NODE_NAME, ALLOW_GROUPS_NODE_NAME];

const shouldIncludeTextNode = (node: PackageItemTextNodeDisplayModel): boolean => {
  // Always include editable nodes
  if (node.editable) return true;
  // Include multi-image download nodes (settings and selection)
  if (MULTI_IMAGE_NODE_NAMES.includes(node.name)) return true;
  return false;
};

export const mapCartPackageItem = (item: PackageItemDisplayModel): Cartv2PackageItemDisplayModel => ({
  ...item,
  id: item.id,
  // packageItemId: item.id,
  displayName: item.altName || item.name,
  imageNodes: item.imageNodes.filter((i) => !i.static && isValidImageNode(i)).map((n) => mapImageNode(n)), // , item.id)),
  textNodes: item.textNodes.filter(shouldIncludeTextNode).map((n) => mapTextNode(n)),
  imagesDirectory: item.imagesDirectory ?? null,
});

export const applySubjectImageToCartPackageItem = (
  img: SubjectImageDisplayModel,
  item: Cartv2PackageItemDisplayModel,
): Cartv2PackageItemDisplayModel => ({
  ...item,
  imageNodes: item.imageNodes.map((i) => ({
    ...i,
    subjectImageId: isAutoFilledIndividualImageNode(i) ? img.id : null,
    groupImageId: null,
    // cropDimensions: null,
    imageThumbnailUrl: isAutoFilledIndividualImageNode(i) ? img.imageThumbnailUrl : null,
  })),
});

/**
 * Set both image ids on a node from a chosen subject image, mirroring the proven
 * per-item `handleChangeImage` logic: `subjectImageId = img.id` and
 * `groupImageId = img.createdFromGroupImageId` (null for individual portraits; the
 * true GroupImage id for subject-attached group photos, which the backend mapper
 * uses to resolve the image URL).
 */
const withSubjectImage = (
  img: SubjectImageDisplayModel,
  node: CartPackageImageNodeDisplayModel,
): CartPackageImageNodeDisplayModel => ({
  ...node,
  subjectImageId: img.id,
  groupImageId: img.createdFromGroupImageId ?? null,
  imageThumbnailUrl: img.imageThumbnailUrl,
  imageUrl: img.imageUrl,
});

/**
 * Step 3 (RSPO hosted): apply ONE chosen individual portrait to EVERY individual image
 * node across an item, leaving group nodes untouched. Non-clobbering counterpart to
 * `applySubjectImageToCartPackageItem` (which fills only auto-fill nodes). Composes
 * with `applyGroupImageToItemNodes` for mixed packages.
 */
export const applyIndividualImageToItemNodes = (
  img: SubjectImageDisplayModel,
  item: Cartv2PackageItemDisplayModel,
): Cartv2PackageItemDisplayModel => ({
  ...item,
  imageNodes: item.imageNodes.map((node) => (isIndividualImageNode(node) ? withSubjectImage(img, node) : node)),
});

/**
 * Step 3 (RSPO hosted): apply ONE chosen group photo (as it appears on the subject —
 * a SubjectImage whose `createdFromGroupImageId` is the true GroupImage id) to EVERY
 * group image node across an item, leaving individual nodes untouched.
 */
export const applyGroupImageToItemNodes = (
  img: SubjectImageDisplayModel,
  item: Cartv2PackageItemDisplayModel,
): Cartv2PackageItemDisplayModel => ({
  ...item,
  imageNodes: item.imageNodes.map((node) => (isGroupImageNode(node) ? withSubjectImage(img, node) : node)),
});

/**
 * @param subject The subject record itself, when the caller has it. Column-backed names
 * (Password, name, id) resolve from the record first and only fall back to `fields`; see
 * `getSubjectDataFieldValue`.
 */
export const fillPackageTextNodes = (
  fields: (SubjectFieldDisplayModel | SubjectFieldCreateModel)[],
  item: Cartv2PackageItemDisplayModel,
  options: TextNodeFillOptions = {},
  subject?: SubjectCoreColumns,
): Cartv2PackageItemDisplayModel => ({
  ...item,
  textNodes: item.textNodes.map((node) => {
    // Don't auto-fill settings nodes - they contain configuration, not subject data
    if (SETTINGS_NODE_NAMES.includes(node.name)) {
      return node;
    }
    return {
      ...node,
      ...fillTextNode(node, fields, options, subject),
    };
  }),
});

/**
 * @param options Fill behavior for the item's text nodes. Consumer ordering passes
 * `skipEmailAutoFill` so the customer is prompted for their own email; the studio build flows
 * (Entry, image matching) omit it and keep auto-filling from subject data.
 */
export const getCartPackageFromPackageAndImage = (
  pkg: PackageDisplayModel,
  subject: SubjectDisplayModel | EntrySubjectModel,
  img?: SubjectImageDisplayModel,
  options: TextNodeFillOptions = {},
): Cartv2PackageDisplayModel => {
  const model: Cartv2PackageDisplayModel = {
    ...(pkg as IPackageModelProperties),
    packageId: pkg.id,
    quantity: 1,
    price: pkg.retailCost,
    subjectName: `${subject.firstName} ${subject.lastName}`,
    subjectLastName: subject.lastName ?? '',
    subjectFirstName: subject.firstName ?? '',
    subjectId: subject.id,
    jobId: subject.jobId!,
    description: pkg.altDescription || 'Prints for package',
    // Stamped from the package's server-computed digital flag ('' is TaxableProductClass.PRINT) so
    // a digital-only cart is recognised before its first save. The server recomputes this from
    // ProductLookup.ShipTier on save, so it stays authoritative.
    taxableProductClass: pkg.isDigital ? 'Digital' : '',
    code: '',
    greenScreen: undefined,
    background: undefined,
    packageName: pkg.name,
    cartPackageItems: pkg.items.map((i) =>
      fillPackageTextNodes(subject.fields ?? [], mapCartPackageItem(i), options, subject),
    ),
    needsReview: false,
  };
  if (img) {
    model.cartPackageItems = model.cartPackageItems.map((i) => applySubjectImageToCartPackageItem(img, i));
  }
  return model;
};

/**
 * @param options Fill behavior for the item's text nodes — pass `consumerTextNodeFillOptions(isV3)`
 * from a consumer surface so a V3 delivery email stays visible for editing while legacy keeps
 * hiding its imported fields.
 */
export const fillAutoFiledPackageTextNodes = (
  fields: SubjectFieldDisplayModel[],
  item: Cartv2PackageItemDisplayModel,
  hasImportedData: boolean,
  options: TextNodeFillOptions = {},
  subject?: SubjectCoreColumns,
): Cartv2PackageItemDisplayModel => ({
  ...item,
  textNodes: item.textNodes.map((node) => {
    // Don't auto-fill settings nodes - they contain configuration, not subject data
    if (SETTINGS_NODE_NAMES.includes(node.name)) {
      return node;
    }
    return {
      ...node,
      ...fillAutoFilledTextNode(node, fields, hasImportedData, options, subject),
    };
  }),
});

/**
 * Merges multi-image settings from the original PackageItemTextNode into the CartPackageTextNode.
 * The CartPackageTextNode.value may be empty (from old cart data), but the PackageItemTextNode.text
 * has the authoritative settings value.
 */
const mergeSettingsFromPackageItem = (
  cartItem: Cartv2PackageItemDisplayModel,
  packageItem: PackageItemDisplayModel | undefined,
): Cartv2PackageItemDisplayModel => {
  if (!packageItem) return cartItem;

  return {
    ...cartItem,
    textNodes: cartItem.textNodes.map((cartNode) => {
      // Only merge for settings nodes
      if (!SETTINGS_NODE_NAMES.includes(cartNode.name)) return cartNode;

      // Find the matching package item text node
      const pkgNode = packageItem.textNodes.find((n) => n.id === cartNode.packageItemTextNodeId);
      if (!pkgNode) return cartNode;

      // Use the package item's text as the value (this is the authoritative setting)
      const mergedValue = pkgNode.text ?? '';

      return {
        ...cartNode,
        value: mergedValue,
      };
    }),
  };
};

export const getCartPackageFromServer = (
  pkg: Cartv2PackageDisplayModel,
  subject: SubjectDisplayModel,
  hasImportedData: boolean,
  jobPackage?: PackageDisplayModel,
  options: TextNodeFillOptions = {},
): Cartv2PackageDisplayModel => {
  return {
    ...pkg,
    cartPackageItems: pkg.cartPackageItems.map((cartItem) => {
      // Find the matching package item by looking at cartItem's relationship to the job package
      const packageItem = jobPackage?.items.find(
        (pi) =>
          cartItem.textNodes.some((tn) =>
            jobPackage.items.some((pItem) =>
              pItem.textNodes.some((ptn) => ptn.id === tn.packageItemTextNodeId && pItem.id === pi.id),
            ),
          ) || cartItem.imageNodes.some((in_) => pi.imageNodes.some((pin) => pin.id === in_.packageItemImageNodeId)),
      );

      let processed = fillAutoFiledPackageTextNodes(subject.fields, cartItem, hasImportedData, options, subject);
      processed = mergeSettingsFromPackageItem(processed, packageItem);
      return processed;
    }),
  };
};
